import React, { useState, useRef, useEffect } from 'react';
import { FileUploadService } from '../utils/fileUploadService';
import { FileUploadResponse, FileSubmission } from '../utils/types';
import { useAuth } from '../contexts/AuthContext';

interface SingleFileUploadProps {
  teamId: string;
  submissionType: "document" | "video" | "photo";
  title: string;
  description: string;
  icon: string;
  acceptedTypes: string;
  currentFile?: FileSubmission | null;
  onUploadComplete?: (response: FileUploadResponse) => void;
  onUploadError?: (error: string) => void;
  onFileDeleted?: () => void;
}

interface UploadProgress {
  stage: string;
  progress: number;
}

const SingleFileUpload: React.FC<SingleFileUploadProps> = ({
  teamId,
  submissionType,
  title,
  description,
  icon,
  acceptedTypes,
  currentFile,
  onUploadComplete,
  onUploadError,
  onFileDeleted
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset selected file when current file changes
  useEffect(() => {
    if (currentFile) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [currentFile]);

  const handleFileSelect = (file: File) => {
    // Validate file
    const validationError = FileUploadService.validateFile(file, submissionType);
    if (validationError) {
      onUploadError?.(validationError);
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !token) return;

    setUploading(true);
    setUploadProgress({ stage: "Preparing...", progress: 0 });

    try {
      const response = await FileUploadService.uploadFile(
        teamId,
        selectedFile,
        submissionType,
        token,
        (stage: string, progress: number) => {
          setUploadProgress({ stage, progress });
        }
      );

      onUploadComplete?.(response);
      setSelectedFile(null);
      setUploadProgress(null);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      onUploadError?.(errorMessage);
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!currentFile || !token) return;

    setDownloading(true);
    try {
      const response = await FileUploadService.getDownloadUrl(
        teamId,
        currentFile.id,
        token
      );
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = currentFile.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Download failed';
      onUploadError?.(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentFile || !token) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete "${currentFile.original_filename}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;

    setDeleting(true);
    try {
      await FileUploadService.deleteSubmission(teamId, currentFile.id, token);
      onFileDeleted?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Delete failed';
      onUploadError?.(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setUploadProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const hasFile = !!currentFile;
  const canUpload = !hasFile && !uploading;

  return (
    <div style={{
      border: '2px solid #e0e0e0',
      borderRadius: '12px',
      padding: '20px',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '15px',
        paddingBottom: '10px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <span style={{ fontSize: '24px', marginRight: '10px' }}>{icon}</span>
        <div>
          <h4 style={{ margin: 0, color: '#333' }}>{title}</h4>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>{description}</p>
        </div>
      </div>

      {/* Current File Display */}
      {hasFile && (
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '15px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                {FileUploadService.getFileTypeIcon(currentFile.submission_type)} {currentFile.original_filename}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {FileUploadService.formatFileSize(currentFile.file_size)} • 
                Uploaded {formatDate(currentFile.uploaded_at)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleDownload}
                disabled={downloading}
                style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  opacity: downloading ? 0.6 : 1
                }}
              >
                {downloading ? '⏳' : '⬇️'} Download
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? '⏳' : '🔄'} Replace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section - Only show if no current file or user wants to replace */}
      {canUpload && (
        <>
          {/* Drop Zone */}
          <div
            className="drop-zone"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragActive ? '#007bff' : '#ccc'}`,
              borderRadius: '8px',
              padding: '30px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: dragActive ? '#f0f8ff' : '#fafafa',
              transition: 'all 0.2s ease'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              accept={acceptedTypes}
              style={{ display: 'none' }}
            />
            
            {selectedFile ? (
              <div>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                  {FileUploadService.getFileTypeIcon(submissionType)} {selectedFile.name}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {FileUploadService.formatFileSize(selectedFile.size)}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                  {dragActive ? '📂 Drop file here' : '📁 Click to select or drag file here'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {acceptedTypes} • Max {submissionType === 'video' ? '500MB' : '100MB'}
                </div>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div style={{ margin: '15px 0' }}>
              <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                {uploadProgress.stage}
              </div>
              <div style={{
                width: '100%',
                backgroundColor: '#e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div
                  style={{
                    width: `${uploadProgress.progress}%`,
                    backgroundColor: '#28a745',
                    height: '6px',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {uploadProgress.progress}%
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {selectedFile && !uploading && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '10px',
              marginTop: '15px'
            }}>
              <button
                onClick={handleUpload}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                🚀 Upload {title}
              </button>
              <button
                onClick={clearFile}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                ❌ Clear
              </button>
            </div>
          )}

          {uploading && (
            <div style={{ 
              textAlign: 'center',
              color: '#007bff', 
              fontSize: '14px',
              marginTop: '15px'
            }}>
              ⏳ Uploading {title.toLowerCase()}... Please wait
            </div>
          )}
        </>
      )}

      {/* Replace file hint */}
      {hasFile && !selectedFile && (
        <div style={{
          textAlign: 'center',
          fontSize: '12px',
          color: '#666',
          fontStyle: 'italic'
        }}>
          Click "Replace" above to upload a new {title.toLowerCase()}
        </div>
      )}
    </div>
  );
};

export default SingleFileUpload;
